import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Layout, ConfigProvider, Spin } from 'antd'
import enUS from 'antd/locale/en_US';
import { AuthProvider, useAuth } from '@contexts/AuthContext'
import ProtectedRoute from '@components/ProtectedRoute'
import NotFoundPage from '@pages/404'

// Lazy load components for code splitting
const Home = lazy(() => import('@pages/home'))
const Login = lazy(() => import ('@pages/Login'))
const Signup = lazy(() => import('@pages/Signup'))
const TestSampler = lazy(() => import('@/pages/testSampler/TestSampler'))
const SampleExamInterface = lazy(() => import('@pages/testSampler/SampleExamInterface'))
const SampleReport = lazy(() => import('@pages/testSampler/SampleReport'))
// Admin/Teacher pages for management
const Dashboard = lazy(() => import('@pages/admin/Dashboard'))
const StudentManage = lazy(() => import('@/pages/admin/StudentManage'))
const PromoStudentManage = lazy(() => import('@/pages/admin/PromoStudentManage'))
const TeachersManage = lazy(() => import('@pages/admin/TeacherManage'))
const QuestionManage = lazy(() => import('@/pages/admin/QuestionManage'))
const AssignmentManage = lazy(() => import('@pages/admin/AssignmentManage'))
const ReadingManage = lazy(() => import('@pages/admin/ReadingManage'))
const AIReviewGenerator = lazy(() => import('@pages/admin/AIReviewGenerator'))
// Admin/Teacher pages for classes
const ClassManage = lazy(() => import('@/pages/admin/classes/ClassManage'))
const ClassStudents = lazy(() => import('@/pages/admin/classes/ClassStudents'))
const ClassAssignments = lazy(() => import('@/pages/admin/classes/ClassAssignments'))
const ClassAssignmentQuestions = lazy(() => import('@/pages/admin/classes/ClassAssignmentQuestions'))
const ClassAssignmentGradebook = lazy(() => import('@/pages/admin/classes/ClassAssignmentGradebook'))
const ClassStudentSubmissions = lazy(() => import('@/pages/admin/classes/ClassStudentSubmissions'))
const ClassStudentReport = lazy(() => import('@/pages/admin/classes/ClassStudentReport'))
// const ReadingManage = lazy(() => import('@pages/admin/ReadingManage'))
// Student pages for assignments and readings
const ExamList = lazy(() => import('@pages/student/assignments/ExamList'))
const ExamInterface = lazy(() => import('@pages/student/assignments/ExamInterface'))
const ExamReview = lazy(() => import('@pages/student/assignments/ExamReview'))
const ReadingList = lazy(() => import('@pages/student/reading/ReadingList'))
const Reading = lazy(() => import('@pages/student/reading/Reading'))
const ReadingConversation = lazy(() => import('@pages/student/reading/ReadingConversation'))
const ReadingQuiz = lazy(() => import('@pages/student/reading/ReadingQuiz'))
// Rewards pages
const RewardsDashboard = lazy(() => import('@pages/student/RewardsDashboard'))
const RewardsManagement = lazy(() => import('@pages/admin/RewardsManagement'))
// Test pages for new ui component
const Test = lazy(() => import('@/pages/ui-test/test'))

// ... other lazy loaded components
const StudentReview = lazy(() => import('@/pages/admin/StudentReview')); // <-- Import the new page
// ...

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh' 
  }}>
    <Spin size="large" />
  </div>
);

const App = () => {
  return (
    <ConfigProvider locale={enUS}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Layout style={{ minHeight: '100vh' }}>
              <Routes>
                {/* Public Routes */}
                <Route path="" element={<Navigate to="/home" />} />
                <Route path="/home" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/test-sampler" element={<TestSampler />} />
                <Route path="/test-sampler/:testType/:subject/:grade/:sampleId" element={<SampleExamInterface />} />
                <Route path="/test-sampler/report/:testType/:subject/:grade/:sampleId" element={<SampleReport />} />
                <Route path="/404" element={<NotFoundPage />} />
                
                {/* Admin/Teacher Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/studentManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <StudentManage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/promoStudentManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <PromoStudentManage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacherManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <TeachersManage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/classManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassManage />
                    </ProtectedRoute>
                  }
                />  
                <Route
                  path="/classManage/:classId/students"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/classManage/:classId/assignments"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassAssignments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/questionManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <QuestionManage />
                    </ProtectedRoute>
                  }
                />  
                <Route
                  path="/assignmentManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <AssignmentManage />
                    </ProtectedRoute>
                  }
                />  
                <Route
                  path="/readingManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ReadingManage />
                    </ProtectedRoute>
                  }
                />  
                <Route
                  path="/classManage/:classId/assignments"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassAssignments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/classManage/:classId/assignments/:assignmentId/questions"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassAssignmentQuestions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/classManage/:classId/assignments/:assignmentId/gradebook"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassAssignmentGradebook />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/classManage/:classId/students/:studentId/submissions"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassStudentSubmissions />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/classManage/:classId/studentReport"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <ClassStudentReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/rewards-management"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <RewardsManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Student Routes */}
                <Route
                  path="/student/assignments"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher','promo_t1','promo_t2']}>
                      <ExamList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exam/:examId/take"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher','promo_t1','promo_t2']}>
                      <ExamInterface />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exam/:examId/review"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher','promo_t1','promo_t2']}>
                      <ExamReview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/reading"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher']}>
                      <ReadingList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/reading/:readingId"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher']}>
                      <Reading />
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path="/student/pdfReading/:readingId"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher']}>
                      <PDFReading />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path="/student/reading/quiz/:readingId/:readingName"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher']}>
                      <ReadingQuiz />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher']}>
                      <RewardsDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/reading/conversation/:readingId/:readingName"
                  element={
                    <ProtectedRoute allowedRoles={['student','admin','teacher']}>
                      <ReadingConversation />
                    </ProtectedRoute>
                  }
                />
                {/* Test pages for new ui component */}
                <Route path="/ui-test" element={
                  <ProtectedRoute allowedRoles={['admin']}><Test /></ProtectedRoute>
                } />
                 <Route
                  path="/dashboard"
                  // ...
                />
                 <Route
                  path="/dashboard"
                  // ...
                />
                <Route
                  path="/studentManage" // Keep existing student management if separate
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <StudentManage />
                    </ProtectedRoute>
                  }
                />
                 <Route
                  path="/studentReview" // <-- Add route for the new page
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <StudentReview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/studentReview/:studentId" 
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                      <StudentReview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacherManage"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <TeachersManage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-review-generator"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AIReviewGenerator />
                    </ProtectedRoute>
                  }
                />

                {/* Catch-all route for unmatched paths */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  )
}

export default App
